import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Search, History, Filter, Download, User, Calendar, Monitor } from 'lucide-react';

const AuditLogs = () => {
  const { user } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    module: '',
    action: '',
    start_date: '',
    end_date: '',
    limit: 100
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [logsRes, modulesRes] = await Promise.all([
        axios.get(`${API}/audit-logs`),
        axios.get(`${API}/audit-logs/modules`)
      ]);
      setLogs(logsRes.data);
      setModules(modulesRes.data.modules);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.module) params.append('module', filters.module);
      if (filters.action) params.append('action', filters.action);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('limit', filters.limit);

      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Error al filtrar');
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-700',
      update: 'bg-blue-100 text-blue-700',
      delete: 'bg-red-100 text-red-700',
      login: 'bg-purple-100 text-purple-700',
      logout: 'bg-slate-100 text-slate-700',
      view: 'bg-cyan-100 text-cyan-700',
      export: 'bg-orange-100 text-orange-700'
    };
    const labels = {
      create: 'Creó',
      update: 'Modificó',
      delete: 'Eliminó',
      login: 'Ingresó',
      logout: 'Salió',
      view: 'Vió',
      export: 'Exportó'
    };
    return (
      <span className={`status-badge ${colors[action] || 'bg-slate-100 text-slate-700'}`}>
        {labels[action] || action}
      </span>
    );
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('es-BO'),
      time: date.toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })
    };
  };

  return (
    <div data-testid="audit-logs-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Historial de Auditoría
          </h1>
          <p className="text-slate-600 mt-2">Registro de todas las acciones del sistema</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6">
        <div className="flex items-center mb-4">
          <Filter size={20} className="text-orange-500 mr-2" />
          <h3 className="font-semibold text-slate-800">Filtros</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Módulo</label>
            <select
              value={filters.module}
              onChange={(e) => setFilters({ ...filters, module: e.target.value })}
              className="input-field"
            >
              <option value="">Todos</option>
              {Object.entries(modules).map(([key, name]) => (
                <option key={key} value={key}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Acción</label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="input-field"
            >
              <option value="">Todas</option>
              <option value="create">Crear</option>
              <option value="update">Modificar</option>
              <option value="delete">Eliminar</option>
              <option value="login">Ingreso</option>
              <option value="export">Exportar</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Desde</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Hasta</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="input-field"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleFilter}
              className="btn-primary w-full"
            >
              <Search size={18} className="mr-2" />
              Filtrar
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Fecha/Hora</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Usuario</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Acción</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Módulo</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Recurso</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const { date, time } = formatTimestamp(log.timestamp);
                return (
                  <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <Calendar size={16} className="text-slate-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">{date}</p>
                          <p className="text-xs text-slate-500">{time}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <User size={16} className="text-slate-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">{log.user_name}</p>
                          <p className="text-xs text-slate-500">{log.user_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getActionBadge(log.action)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {modules[log.module] || log.module}
                    </td>
                    <td className="px-4 py-3">
                      {log.resource_name ? (
                        <span className="text-sm text-slate-800">{log.resource_name}</span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <Monitor size={14} className="text-slate-400 mr-1" />
                        <span className="text-xs text-slate-500 mono">{log.ip_address || '-'}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {logs.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <History size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No hay registros de auditoría</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
